import { autoUpdate, computePosition, flip, offset } from "@floating-ui/dom";
import { Dom } from "../../../common/dom";
import { Txt } from "../../../common/text";
import { Helper } from "../../../common/helper";

export class ImagePreview
{
    private readonly anchor: HTMLElement;
    private popoverEl: HTMLElement | null = null;
    private imageEl: HTMLImageElement | null = null;
    private cleanupAutoUpdate: (() => void) | null = null;
    private active = false;
    private configureVersion = 0;

    private readonly onEnter = (): void => this.show();
    private readonly onLeave = (): void => this.hide();

    constructor(anchor: HTMLElement)
    {
        this.anchor = anchor;
    }

    configure(file?: File): void
    {
        // Bump the version so a slower, now-stale FileReader from a previous
        // call can't bring back the wrong preview when its onload finally fires.
        const version = ++this.configureVersion;
        if (file && Helper.isImage(file.name))
        {
            const reader = new FileReader();
            reader.onload = e =>
            {
                if (version === this.configureVersion)
                {
                    this.activate(e.target.result as string);
                }
            };
            reader.readAsDataURL(file);
        }
        else
        {
            this.deactivate();
        }
    }

    /** Fully tear down: remove listeners, drop the popover element and stop positioning. */
    dispose(): void
    {
        // Invalidate any in-flight FileReader so its onload can't rebuild the preview.
        this.configureVersion++;
        this.deactivate();
        if (this.popoverEl)
        {
            this.popoverEl.remove();
            this.popoverEl = null;
            this.imageEl = null;
        }
    }

    private activate(image: string): void
    {
        if (!this.popoverEl)
        {
            this.build();
        }
        this.imageEl.src = image;
        if (!this.active)
        {
            this.anchor.addEventListener("mouseenter", this.onEnter);
            this.anchor.addEventListener("mouseleave", this.onLeave);
            this.active = true;
        }
        this.show();
    }

    private deactivate(): void
    {
        if (this.active)
        {
            this.anchor.removeEventListener("mouseenter", this.onEnter);
            this.anchor.removeEventListener("mouseleave", this.onLeave);
            this.active = false;
        }
        this.hide();
    }

    private show(): void
    {
        if (!this.popoverEl)
        {
            return;
        }
        this.popoverEl.style.display = "block";
        this.stopAutoUpdate();
        this.cleanupAutoUpdate = autoUpdate(this.anchor, this.popoverEl, () =>
        {
            computePosition(this.anchor, this.popoverEl, {
                placement: "bottom",
                // No shift(): keeps the popover centred so the CSS arrow keeps pointing at the anchor.
                middleware: [offset(10), flip()]
            }).then(position =>
            {
                // The popover may have been disposed while this promise was pending.
                if (!this.popoverEl)
                {
                    return;
                }
                this.popoverEl.style.left = position.x + "px";
                this.popoverEl.style.top = position.y + "px";
                const base = position.placement.split("-")[0];
                this.popoverEl.classList.remove("w-image-preview-top", "w-image-preview-bottom");
                this.popoverEl.classList.add("w-image-preview-" + base);
            });
        });
    }

    private hide(): void
    {
        this.stopAutoUpdate();
        if (this.popoverEl)
        {
            this.popoverEl.style.display = "none";
        }
    }

    private build(): void
    {
        this.popoverEl = Dom.createDiv("w-image-preview", "w-image-preview-bottom");
        this.popoverEl.style.position = "absolute";
        this.popoverEl.style.top = "0";
        this.popoverEl.style.left = "0";

        Dom.addDiv(this.popoverEl, "w-image-preview-arrow");
        Dom.addDiv(this.popoverEl, "w-image-preview-title").textContent = Txt.imagePreview;

        const content = Dom.addDiv(this.popoverEl);
        content.style.padding = "9px 14px";

        this.imageEl = Dom.add("img", content);
        this.imageEl.style.maxWidth = "250px";
        this.imageEl.style.maxHeight = "250px";

        // Append inside the anchor's <dialog> if there is one, otherwise <body>:
        // a body-level element renders behind a modal dialog's top layer.
        (this.anchor.closest("dialog") || document.body).appendChild(this.popoverEl);
    }

    private stopAutoUpdate(): void
    {
        if (this.cleanupAutoUpdate)
        {
            this.cleanupAutoUpdate();
            this.cleanupAutoUpdate = null;
        }
    }
}
